/* eslint-disable @nx/enforce-module-boundaries */
import { ExclamationIcon, LinkIcon } from '@mybridge/icons';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  Image,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mybridge/ui';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { forwardRef, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import TruncatedTextWithTooltip from 'v4/components/common/Tooltip';
import { getUserFullName } from 'v4/lib/commons';
export const SupportForm = forwardRef(
  ({ onSubmit: onSubmit_, isLoading, onBackClick, ...props }, ref) => {
    const fileRef = useRef();
    const { t } = useTranslator();
    const [file, setFile] = useState();
    const [src, setSrc] = useState();
    const {
      register,
      handleSubmit,
      control,
      setValue,
      watch,
      formState: { errors },
    } = useForm();

    const { userProfileInfo } = useSelector((s) => s.userProfile);
    const fName = getUserFullName(userProfileInfo);

    const onSubmit = (form) => {
      onSubmit_?.(form);
    };

    const handleFileSelect = (e) => {
      const file_ = e?.target?.files?.[0];
      if (file_?.type?.startsWith('image/')) {
        const fr = new FileReader();
        fr.onload = () => {
          setSrc(fr.result);
        };
        fr.readAsDataURL(file_);
      }

      setFile(file_);
      setValue('attachments', file_);
    };

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack {...props}>
          <Heading size="lg">
            {fName?.length ? fName : `${t('support.hello') || "Hello"}, `} {t('support.welcomeMessage') || "we’re here to help"}
          </Heading>
          <Text>
            {t('support.importantInfoNote') || "Share the most important info with us. After you open a request, you can add updates via My support requests."}
          </Text>
          <Card my={4} p={4}>
            <HStack alignItems="flex-start" F>
              <Box>
                <ExclamationIcon width={20} height={20} color="#CCCCCC" />
              </Box>
              <Text flex={1}>
                {t('support.sensitiveInfoWarning') || "Do not share sensitive information (attachments or text).Ex. Your credit card details or personal ID numbers."}
              </Text>
            </HStack>
          </Card>
          <Card p={4} spacing={4} as={Stack}>
            {userProfileInfo?.id ? (
              <></>
            ) : (
              <>
                <FormControl isInvalid={errors.name}>
                  <FormLabel>{t('myDisk.name') || "Name"}*</FormLabel>
                  <Input
                    type="name"
                    placeholder={'eg: John Doe'}
                    {...register('name', { required: true })}
                  />
                  {errors?.name?.type === 'required' ? (
                    <FormErrorMessage>{t('support.nameRequired') || "Your name is required"}</FormErrorMessage>
                  ) : (
                    ''
                  )}
                </FormControl>
                <FormControl isInvalid={errors.from}>
                  <FormLabel>{t('profileDetails.email') || "Email"}*</FormLabel>
                  <Input
                    type="email"
                    placeholder={'eg: john.doe@gmail.com'}
                    {...register('from', {
                      required: true,
                    })}
                  />
                  {errors?.from?.type === 'required' ? (
                    <FormErrorMessage>
                      {t('createPage.pleaseEnterValidEmail') || "Please enter a valid email"}
                    </FormErrorMessage>
                  ) : (
                    ''
                  )}
                </FormControl>
              </>
            )}
            {/* <FormControl>
              <FormLabel>Category*</FormLabel>
              <Select {...register('category', {
                  required: true,
                })}>
                <option value="Question">Question</option>
                <option value="Incident">Incident</option>
                <option value="Problem">Problem</option>
                <option value="Feature Request">Feature Request</option>
                <option value="Refund">Refund</option>
              </Select>
            </FormControl> */}
            <FormControl isInvalid={errors.title}>
              <FormLabel>{t('myEvents.subject') || "Subject"}*</FormLabel>
              <Input
                placeholder={t('support.enterSubject') || 'Enter short subject about the issue'}
                {...register('title', {
                  required: true,
                  minLength: 3,
                  maxLength: 100,
                })}
                maxLength={100}
              />
              <HStack>
                <Stack flex={1}>
                  {errors?.title?.type === 'required' ? (
                    <FormErrorMessage>{t('support.subjectRequired') || "Please enter a subject"}</FormErrorMessage>
                  ) : (
                    ''
                  )}
                  {errors?.title?.type === 'minLength' ? (
                    <FormErrorMessage>
                      {t('support.subjectMinLength') || "Should be atleast 3 characters long"}
                    </FormErrorMessage>
                  ) : (
                    ''
                  )}
                  {errors?.title?.type === 'maxLength' ? (
                    <FormErrorMessage>
                      {t('support.subjectMaxLength') || "Shouldn't be more than 100 characters"}
                    </FormErrorMessage>
                  ) : (
                    ''
                  )}
                </Stack>
                <Stack>
                  <FormHelperText>{watch('title')?.length}/100</FormHelperText>
                </Stack>
              </HStack>
            </FormControl>
            <FormControl isInvalid={errors.message}>
              <FormLabel>{t('myCalendar.description') || "Description"}*</FormLabel>
              <Textarea
                placeholder={
                  t('support.descriptionHelp') || 'By describing the most important facts, we can quickly resolve your request.'
                }
                {...register('message', {
                  required: true,
                  minLength: 3,
                  maxLength: 500,
                })}
                maxLength={500}
              />
              <HStack>
                <Stack flex={1}>
                  {errors?.message?.type === 'required' ? (
                    <FormErrorMessage>
                      {t('support.descriptionRequired') || "Please enter a description"}
                    </FormErrorMessage>
                  ) : (
                    ''
                  )}
                  {errors?.message?.type === 'minLength' ? (
                    <FormErrorMessage>
                      {t('support.descriptionMinLength') || "Should be atleast 3 characters long"}
                    </FormErrorMessage>
                  ) : (
                    ''
                  )}
                  {errors?.message?.type === 'maxLength' ? (
                    <FormErrorMessage>
                      {t('support.descriptionMaxLength') || "Shouldn't be more than 500 characters"}
                    </FormErrorMessage>
                  ) : (
                    ''
                  )}
                </Stack>
                <Stack>
                  <FormHelperText>
                    {watch('message')?.length}/500
                  </FormHelperText>
                </Stack>
              </HStack>
            </FormControl>
            <HStack justifyContent="center">
              {file?.type?.startsWith('image/') ? (
                <Image maxH="48" src={src} />
              ) : (
                ''
              )}
            </HStack>
            <Button
              borderRadius={"30px"}
              variant="secondary"
              leftIcon={<LinkIcon width={20} height={20} />}
              onClick={(e) => fileRef?.current?.click?.()}
            >
              {/* {file?.name ?? (t('support.attachFiles') || 'Attach Files')} */}
              {file?.name ? (
                <TruncatedTextWithTooltip 
                  text={file.name}
                  maxChars={40}
                  fontSize="inherit"
                  showTooltip={true}
                />
              ) : (
                t('support.attachFiles') || 'Attach Files'
              )}
              <Input
                onChange={handleFileSelect}
                ref={fileRef}
                display="none"
                type="file"
              />
            </Button>

            <HStack>
              <Button borderRadius={"30px"} onClick={onBackClick} flex={1} variant="secondary">
                {t('buttons.back') || 'Back'}
              </Button>
              <Button
                isLoading={isLoading}
                type="submit"
                flex={1}
                variant="primary"
                borderRadius={"30px"}
              >
                {t('support.sendRequest') || "Send Request"}
              </Button>
            </HStack>
          </Card>
        </Stack>
      </form>
    );
  }
);
